import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowLeft } from "lucide-react";
import { fmtCurrency } from "../lib/formatters";

// Dave 2026-08-06 · Year-total-expense drill-down popover
// -----------------------------------------------------------------------
// Compact floating card that lists the individual elements which make up
// a single year's total expenditure, sorted descending by inflated
// amount. Reused in two places:
//
//   1. The Waterfall chart's "Expenses" per-year detail popover — clicking
//      any row's inflated amount opens this popover as a CASCADE to the
//      right (or left if there's no room), with an optional "back" arrow.
//   2. The Projections screen's Expenditures row — clicking any non-zero
//      year cell opens this popover anchored to the cell.
//
// Portaled to document.body with `position: fixed` so it renders OUTSIDE
// any ancestor with `overflow: hidden` (e.g. the Waterfall's own popover
// container) and can freely overflow into any part of the viewport.
// Callers therefore pass VIEWPORT-relative anchor coordinates
// (`getBoundingClientRect().left / top / right / bottom` directly, no
// container-relative adjustment).
//
// Design intent (per Dave's answers 2026-08-06):
//   * Sort DESCENDING by inflated amount per year
//   * Rows show element name + inflated amount only (no % / no base cost)
//   * Compact — 260-320px wide, capped ~360px tall with internal scroll
//   * Subtle click affordance on the source trigger (underline + pointer)
//   * Outside-click and Escape dismiss

const POPOVER_WIDTH = 300;
const POPOVER_MAX_HEIGHT = 360;

export default function YearExpensesPopover({
    year,
    elements,
    total,
    anchor,       // { x, y, flipOffset? } in VIEWPORT coordinates
    onClose,
    onBack,       // optional — renders back-arrow if provided
    testIdSuffix = "",
}) {
    const ref = useRef(null);
    const [placedStyle, setPlacedStyle] = useState({
        position: "fixed",
        left: anchor?.x ?? 0,
        top: anchor?.y ?? 0,
        zIndex: 70,
        opacity: 0,
    });

    // Outside-click and Escape to dismiss.
    useEffect(() => {
        const handleClick = (e) => {
            if (!ref.current) return;
            if (!ref.current.contains(e.target)) onClose();
        };
        const handleKey = (e) => {
            if (e.key === "Escape") onClose();
        };
        // mousedown so the initial trigger's own onClick can complete first.
        document.addEventListener("mousedown", handleClick);
        document.addEventListener("keydown", handleKey);
        return () => {
            document.removeEventListener("mousedown", handleClick);
            document.removeEventListener("keydown", handleKey);
        };
    }, [onClose]);

    // Sort descending by amount, filter out zero-value elements so the list
    // shows only what actually got replaced that year.
    const sorted = useMemo(() => {
        return (elements || [])
            .filter((el) => (el.amount || 0) > 0.5)
            .slice()
            .sort((a, b) => (b.amount || 0) - (a.amount || 0));
    }, [elements]);

    // After mount, measure the popover and clamp inside the viewport. If
    // placing at anchor.x would push us off the right edge, flip so the
    // popover sits to the LEFT of the trigger using `flipOffset` (the
    // horizontal distance the caller wants us to travel when flipping —
    // typically the trigger's own width + a gap).
    useLayoutEffect(() => {
        if (!ref.current || !anchor) return;
        const rect = ref.current.getBoundingClientRect();
        const vw = document.documentElement.clientWidth;
        const vh = document.documentElement.clientHeight;
        let left = anchor.x;
        let top = anchor.y;
        if (left + rect.width > vw - 8) {
            const flip = anchor.flipOffset != null ? anchor.flipOffset : rect.width + 16;
            left = Math.max(8, anchor.x - flip - rect.width);
        }
        if (top + rect.height > vh - 8) {
            top = Math.max(8, vh - rect.height - 8);
        }
        left = Math.max(8, Math.min(left, vw - rect.width - 8));
        top = Math.max(8, top);
        setPlacedStyle({
            position: "fixed",
            left,
            top,
            zIndex: 70,
            opacity: 1,
        });
    }, [anchor, sorted.length]);

    if (!anchor) return null;

    return createPortal(
        <div
            ref={ref}
            data-testid={`year-expenses-popover${testIdSuffix ? "-" + testIdSuffix : ""}`}
            className="bg-white shadow-lg"
            style={{
                ...placedStyle,
                width: POPOVER_WIDTH,
                maxHeight: POPOVER_MAX_HEIGHT,
                border: "1px solid #1C4E80",
                borderRadius: 3,
                fontSize: 11,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                transition: "opacity 120ms ease-in",
            }}
            onClick={(e) => e.stopPropagation()}
        >
            {/* Header */}
            <div
                className="px-2.5 py-1.5 border-b border-rule flex items-start justify-between"
                style={{ background: "#F4F7FA" }}
            >
                <div className="flex items-start gap-1.5">
                    {onBack && (
                        <button
                            type="button"
                            onClick={onBack}
                            className="text-slate hover:text-navy pt-0.5"
                            title="Back to yearly expenses"
                            data-testid="year-expenses-popover-back"
                        >
                            <ArrowLeft size={13} />
                        </button>
                    )}
                    <div>
                        <div
                            className="font-display font-semibold text-navy"
                            style={{ fontSize: 12.5, letterSpacing: 0 }}
                        >
                            Year {year} Expenses
                        </div>
                        <div className="text-slate" style={{ fontSize: 10.5 }}>
                            {sorted.length} element{sorted.length === 1 ? "" : "s"} replaced
                        </div>
                        <div
                            className="font-mono tabular-nums mt-0.5"
                            style={{ fontWeight: 600, color: "#EA6A47", fontSize: 13 }}
                            data-testid="year-expenses-popover-total"
                        >
                            {fmtCurrency(total)}
                        </div>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={onClose}
                    className="text-slate hover:text-navy px-1"
                    data-testid="year-expenses-popover-close"
                    title="Close (Esc)"
                >
                    ×
                </button>
            </div>

            {/* Body — element list, descending by amount */}
            <div style={{ overflowY: "auto", flex: 1 }}>
                {sorted.length === 0 ? (
                    <div className="px-2.5 py-2 text-slate italic" style={{ fontSize: 10.5 }}>
                        No individual line items available for {year}.
                    </div>
                ) : (
                    <table
                        className="w-full"
                        style={{ fontSize: 10.5 }}
                        data-testid="year-expenses-popover-table"
                    >
                        <tbody>
                            {sorted.map((el, i) => (
                                <tr
                                    key={`${el.name}-${i}`}
                                    className="border-b border-rule"
                                    data-testid={`year-expenses-popover-row-${i}`}
                                >
                                    <td
                                        className="px-2 py-1 text-ink"
                                        style={{
                                            fontFamily: "'Source Sans Pro', ui-sans-serif, system-ui",
                                            lineHeight: 1.25,
                                        }}
                                    >
                                        {el.name}
                                    </td>
                                    <td
                                        className="px-2 py-1 text-right tabular-nums text-navy"
                                        style={{
                                            fontFamily: "IBM Plex Mono, ui-monospace",
                                            whiteSpace: "nowrap",
                                            width: 92,
                                        }}
                                    >
                                        {fmtCurrency(el.amount)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>,
        document.body
    );
}
